<script setup>
/**
 * Assignments.
 *
 * Assignment policies and agent capacity policies used to live behind a card
 * launcher with a drill-down list each; Intercom keeps one Assignments page,
 * so the two lists now sit in tabs on the shell, `?tab=` deep-linkable the
 * same way Teammates does it. Capacity keeps its premium gate - the tab only
 * appears when both ASSIGNMENT_V2 and ADVANCED_ASSIGNMENT are on. The create
 * and edit forms stay standalone routes on the same shell, keeping a breadcrumb
 * above the form; the old list URLs redirect into the matching tab.
 */
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

import SettingsPageShell from 'dashboard/components-next/Settings/SettingsPageShell.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import AssignmentPolicyCard from 'dashboard/components-next/AssignmentPolicy/AssignmentPolicyCard/AssignmentPolicyCard.vue';
import AgentCapacityPolicyCard from 'dashboard/components-next/AssignmentPolicy/AgentCapacityPolicyCard/AgentCapacityPolicyCard.vue';
import ConfirmDeletePolicyDialog from './pages/components/ConfirmDeletePolicyDialog.vue';

const store = useStore();
const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const accountId = computed(() => Number(route.params.accountId));
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// The route itself is gated by ASSIGNMENT_V2; capacity policies additionally
// need ADVANCED_ASSIGNMENT, the flag that identifies premium accounts.
const showCapacityTab = computed(
  () =>
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.ASSIGNMENT_V2
    ) &&
    isFeatureEnabledonAccount.value(
      accountId.value,
      FEATURE_FLAGS.ADVANCED_ASSIGNMENT
    )
);

const tabs = computed(() => [
  { key: 'policies', label: t('ASSIGNMENT_POLICY.INDEX.TABS.POLICIES') },
  ...(showCapacityTab.value
    ? [{ key: 'capacity', label: t('ASSIGNMENT_POLICY.INDEX.TABS.CAPACITY') }]
    : []),
]);

const activeTab = computed(() => {
  const requested = route.query.tab;
  return tabs.value.some(tab => tab.key === requested) ? requested : 'policies';
});
const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeTab.value)
);
const isCapacityTab = computed(() => activeTab.value === 'capacity');

const onTabChange = tab => {
  if (tab.key === activeTab.value) return;
  const query = { ...route.query };
  delete query.tab;
  if (tab.key !== 'policies') query.tab = tab.key;
  router.push({ name: route.name, query });
};

const assignmentPolicies = useMapGetter(
  'assignmentPolicies/getAssignmentPolicies'
);
const assignmentUiFlags = useMapGetter('assignmentPolicies/getUIFlags');
const inboxUiFlags = useMapGetter('assignmentPolicies/getInboxUiFlags');

const capacityPolicies = useMapGetter(
  'agentCapacityPolicies/getAgentCapacityPolicies'
);
const capacityUiFlags = useMapGetter('agentCapacityPolicies/getUIFlags');
const usersUiFlags = useMapGetter('agentCapacityPolicies/getUsersUIFlags');

const isFetching = computed(() =>
  isCapacityTab.value
    ? capacityUiFlags.value.isFetching
    : assignmentUiFlags.value.isFetching
);

const emptyMessage = computed(() =>
  isCapacityTab.value
    ? t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.NO_RECORDS_FOUND')
    : t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.NO_RECORDS_FOUND')
);

const showEmptyState = computed(() =>
  isCapacityTab.value
    ? !capacityPolicies.value.length
    : !assignmentPolicies.value.length
);

const createButtonLabel = computed(() =>
  isCapacityTab.value
    ? t('ASSIGNMENT_POLICY.AGENT_CAPACITY_POLICY.INDEX.HEADER.CREATE_POLICY')
    : t('ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY.INDEX.HEADER.CREATE_POLICY')
);

const onClickCreatePolicy = () => {
  router.push({
    name: isCapacityTab.value
      ? 'agent_capacity_policy_create'
      : 'agent_assignment_policy_create',
  });
};

const onClickEditPolicy = id => {
  router.push({
    name: isCapacityTab.value
      ? 'agent_capacity_policy_edit'
      : 'agent_assignment_policy_edit',
    params: { id },
  });
};

const handleFetchInboxes = id => {
  if (inboxUiFlags.value.isFetching) return;
  store.dispatch('assignmentPolicies/getInboxes', id);
};

const handleFetchUsers = id => {
  if (usersUiFlags.value.isFetching) return;
  store.dispatch('agentCapacityPolicies/getUsers', id);
};

const confirmDeletePolicyDialogRef = ref(null);

const handleDelete = id => {
  confirmDeletePolicyDialogRef.value.openDialog(id);
};

const handleDeletePolicy = async policyId => {
  const scope = isCapacityTab.value
    ? 'AGENT_CAPACITY_POLICY'
    : 'AGENT_ASSIGNMENT_POLICY';
  const storeModule = isCapacityTab.value
    ? 'agentCapacityPolicies'
    : 'assignmentPolicies';
  try {
    await store.dispatch(`${storeModule}/delete`, policyId);
    useAlert(t(`ASSIGNMENT_POLICY.${scope}.DELETE_POLICY.SUCCESS_MESSAGE`));
    confirmDeletePolicyDialogRef.value.closeDialog();
  } catch (error) {
    useAlert(t(`ASSIGNMENT_POLICY.${scope}.DELETE_POLICY.ERROR_MESSAGE`));
  }
};

onMounted(() => {
  store.dispatch('assignmentPolicies/get');
  if (showCapacityTab.value) store.dispatch('agentCapacityPolicies/get');
});
</script>

<template>
  <SettingsPageShell
    wide
    :title="$t('ASSIGNMENT_POLICY.INDEX.HEADER.TITLE')"
    :description="$t('ASSIGNMENT_POLICY.INDEX.HEADER.DESCRIPTION')"
  >
    <template #actions>
      <Button
        :label="createButtonLabel"
        size="sm"
        @click="onClickCreatePolicy"
      />
    </template>

    <template #tabs>
      <TabBar
        :tabs="tabs"
        :initial-active-tab="activeTabIndex"
        @tab-changed="onTabChange"
      />
    </template>

    <div
      v-if="isFetching"
      role="status"
      aria-busy="true"
      class="flex flex-col gap-5"
    >
      <Skeleton
        v-for="row in 3"
        :key="row"
        variant="text"
        :lines="2"
        :announce="false"
      />
    </div>

    <p
      v-else-if="showEmptyState"
      class="py-20 text-center text-base rounded-xl text-n-slate-11 outline outline-1 outline-n-weak -outline-offset-1"
    >
      {{ emptyMessage }}
    </p>

    <div v-else-if="isCapacityTab" class="flex flex-col gap-4">
      <AgentCapacityPolicyCard
        v-for="policy in capacityPolicies"
        :key="policy.id"
        v-bind="policy"
        :is-fetching-users="usersUiFlags.isFetching"
        @fetch-users="handleFetchUsers"
        @edit="onClickEditPolicy"
        @delete="handleDelete"
      />
    </div>

    <div v-else class="flex flex-col gap-4">
      <AssignmentPolicyCard
        v-for="policy in assignmentPolicies"
        :key="policy.id"
        v-bind="policy"
        :is-fetching-inboxes="inboxUiFlags.isFetching"
        @fetch-inboxes="handleFetchInboxes"
        @edit="onClickEditPolicy"
        @delete="handleDelete"
      />
    </div>

    <ConfirmDeletePolicyDialog
      ref="confirmDeletePolicyDialogRef"
      @delete="handleDeletePolicy"
    />
  </SettingsPageShell>
</template>
